import { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Card,
  Title2,
  Body1,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Spinner,
  Dropdown,
  Option,
  Button,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { Link } from 'react-router-dom';
import { ArrowLeftRegular, ArrowRightRegular, FilterRegular } from '@fluentui/react-icons';
import { apiClient } from '../api/client';
import { DATETIME_FORMAT } from '../config/constants';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
  },
  filters: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalL,
    flexWrap: 'wrap',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  paginationButtons: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  emptyState: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
});

interface HistoryEntry {
  _id: string;
  requestId: string;
  requestType: 'onboarding' | 'offboarding' | 'teams_site' | 'employee_update' | 'hardware';
  action: string;
  actorName: string;
  actorEmail: string;
  timestamp: string;
  requestSnapshot: {
    displayName?: string;
    userDisplayName?: string;
    firstName?: string;
    lastName?: string;
    temporaryPassword?: string;
    createdUserPassword?: string;
  };
}

interface PaginatedResponse {
  data: HistoryEntry[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export function HistoryPage() {
  const styles = useStyles();
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `/requests/history?page=${page}&limit=20`;
      if (typeFilter) url += `&type=${typeFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;

      const response = await apiClient.get<{ data: PaginatedResponse }>(url);
      setHistory(response.data.data);
      setTotalPages(response.data.pagination.pages);
      setTotal(response.data.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [page, typeFilter, statusFilter]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', DATETIME_FORMAT);
  };

  const getStatusColor = (action: string): 'success' | 'danger' | 'warning' | 'informative' | 'subtle' => {
    switch (action) {
      case 'completed':
      case 'approved':
        return 'success';
      case 'denied':
      case 'failed':
        return 'danger';
      case 'created':
        return 'informative';
      case 'auto_disabled':
        return 'warning';
      default:
        return 'subtle';
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'onboarding':
        return <Badge color="brand">Onboarding</Badge>;
      case 'offboarding':
        return <Badge color="danger">Offboarding</Badge>;
      case 'teams_site':
        return <Badge color="informative">Teams</Badge>;
      case 'employee_update':
        return <Badge color="warning">Profile Update</Badge>;
      case 'hardware':
        return <Badge color="informative">Hardware Request</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  const getUserName = (entry: HistoryEntry) => {
    const snapshot = entry.requestSnapshot;
    if (snapshot.displayName) return snapshot.displayName;
    if (snapshot.userDisplayName) return snapshot.userDisplayName;
    if (snapshot.firstName && snapshot.lastName) {
      return `${snapshot.firstName} ${snapshot.lastName}`;
    }
    return '-';
  };

  const getPassword = (entry: HistoryEntry) => {
    // Only show password for completed onboarding entries
    if (entry.requestType !== 'onboarding' || entry.action !== 'completed') {
      return '-';
    }
    const snapshot = entry.requestSnapshot;
    return snapshot.temporaryPassword || snapshot.createdUserPassword || '-';
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title2>Request History</Title2>
        <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
          View all onboarding, offboarding, profile updates, and Teams creation requests
        </Body1>
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.filters}>
        <Dropdown
          placeholder="All Types"
          value={typeFilter ? typeFilter.charAt(0).toUpperCase() + typeFilter.slice(1) : ''}
          onOptionSelect={(_, data) => {
            setTypeFilter(data.optionValue as string);
            setPage(1);
          }}
        >
          <Option value="">All Types</Option>
          <Option value="onboarding">Onboarding</Option>
          <Option value="offboarding">Offboarding</Option>
          <Option value="teams_site">Teams Site</Option>
          <Option value="employee_update">Profile Update</Option>
          <Option value="hardware">Hardware Request</Option>
        </Dropdown>

        <Dropdown
          placeholder="All Statuses"
          value={statusFilter ? statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) : ''}
          onOptionSelect={(_, data) => {
            setStatusFilter(data.optionValue as string);
            setPage(1);
          }}
        >
          <Option value="">All Statuses</Option>
          <Option value="created">Created</Option>
          <Option value="approved">Approved</Option>
          <Option value="denied">Denied</Option>
          <Option value="completed">Completed</Option>
          <Option value="failed">Failed</Option>
        </Dropdown>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <Spinner size="large" label="Loading history..." />
        </div>
      ) : history.length === 0 ? (
        <Card className={styles.emptyState}>
          <FilterRegular style={{ fontSize: '48px', marginBottom: tokens.spacingVerticalM }} />
          <Body1>No history entries found</Body1>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Request ID</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>User</TableHeaderCell>
                <TableHeaderCell>Action</TableHeaderCell>
                <TableHeaderCell>Password</TableHeaderCell>
                <TableHeaderCell>By</TableHeaderCell>
                <TableHeaderCell>Date</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((entry) => (
                <TableRow key={entry._id}>
                  <TableCell>
                    <Link to={`/history/${entry.requestId}`} style={{ color: tokens.colorBrandForeground1 }}>
                      {entry.requestId}
                    </Link>
                  </TableCell>
                  <TableCell>{getTypeBadge(entry.requestType)}</TableCell>
                  <TableCell>{getUserName(entry)}</TableCell>
                  <TableCell>
                    <Badge color={getStatusColor(entry.action)}>
                      {entry.action.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell style={{ fontFamily: 'monospace' }}>{getPassword(entry)}</TableCell>
                  <TableCell>{entry.actorName}</TableCell>
                  <TableCell>{formatDate(entry.timestamp)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className={styles.pagination}>
            <Body1>
              Showing {history.length} of {total} entries
            </Body1>
            <div className={styles.paginationButtons}>
              <Button
                appearance="subtle"
                icon={<ArrowLeftRegular />}
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Body1>
                Page {page} of {totalPages}
              </Body1>
              <Button
                appearance="subtle"
                icon={<ArrowRightRegular />}
                iconPosition="after"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
